import LandingHeader from 'components/Common/LandingHeaderV2';
import React, { useState, useMemo } from 'react';
import classNames from 'classnames';
import useTranslation from 'hooks/useTranslation';
import heroImg from 'assets/images/floor-plan-landing/hero.png';
import { Button } from 'react-bootstrap';
import { ReactComponent as HeroBullet1Icon } from 'assets/images/floor-plan-landing/hero-bullet-1.svg';
import { ReactComponent as HeroBullet2Icon } from 'assets/images/floor-plan-landing/hero-bullet-2.svg';
import { ReactComponent as UseCase1Img } from 'assets/images/floor-plan-landing/usecase1.svg';
import { ReactComponent as Why1Icon } from 'assets/images/floor-plan-landing/why_1.svg';
import { ReactComponent as Why2Icon } from 'assets/images/floor-plan-landing/why_2.svg';
import { ReactComponent as Why3Icon } from 'assets/images/floor-plan-landing/why_3.svg';
import { ReactComponent as Why4Icon } from 'assets/images/floor-plan-landing/why_4.svg';
import { ReactComponent as Why5Icon } from 'assets/images/floor-plan-landing/why_5.svg';
import { ReactComponent as UseCase2Img } from 'assets/images/floor-plan-landing/usecase2.svg';
import { ReactComponent as Hiw1Img } from 'assets/images/floor-plan-landing/hiw1.svg';
import { ReactComponent as Hiw2Img } from 'assets/images/floor-plan-landing/hiw2.svg';
import { ReactComponent as Hiw3Img } from 'assets/images/floor-plan-landing/hiw3.svg';
import feature1Img from 'assets/images/floor-plan-landing/feature1.png';
import feature5Img from 'assets/images/floor-plan-landing/feature5.png';
import LandingFooter from 'components/Common/LandingFooter';
import pricing1Img from 'assets/images/floor-plan-landing/pricing1.png';
import pricing5Img from 'assets/images/floor-plan-landing/pricing5.png';
import bedCam2FinalImg from 'assets/images/floor-plan-landing/bed_cam_2_final.jpg';
import threeDRenderVideo from 'assets/images/floor-plan-landing/3d-render.mp4';
import threeDImage from 'assets/images/floor-plan-landing/3d.jpg';
import interactiveFloorPlanImg from 'assets/images/floor-plan-landing/interactive-floorplan.jpg';
import { ReactComponent as CheckBoxIcon } from 'assets/images/floor-plan-landing/checkbox.svg';
import { ReactComponent as CheckBoxCheckedIcon } from 'assets/images/floor-plan-landing/checkbox-checked.svg';
import vrTourImg from 'assets/images/floor-plan-landing/vr-tour.png';
import { ReactComponent as AppStoreButton } from 'assets/images/floor-plan-landing/appStore.svg';
import styles from './FloorPlanLanding.module.scss';

function PricingOption({ title, description, checked, price, onClick }) {
  return (
    <div className={classNames(styles.pricingOption, { [styles.selected]: checked })} onClick={onClick}>
      {checked ? <CheckBoxCheckedIcon /> : <CheckBoxIcon />}
      <div className={styles.pricingOptionText}>
        <div className={styles.pricingOptionTitle}>
          {title}
        </div>
        {description && (
          <div className={styles.pricingOptionDescription}>
            {description}
          </div>
        )}
      </div>
      <div className={styles.pricingOptionPrice}>
        $
        {price}
      </div>
    </div>
  );
}

function Pricing() {
  const { tFloorPlanLanding } = useTranslation();
  const [selectedOptions, setSelectedOptions] = useState([]);

  const toggleOption = (option) => {
    setSelectedOptions(selectedOptions => {
      if (selectedOptions.includes(option)) {
        return selectedOptions.filter((o) => o !== option);
      }

      return [...selectedOptions, option];
    });
  };

  const ensureOption = (options) => {
    setSelectedOptions(selectedOptions => {
      const newSelectedOptions = [...selectedOptions];
      options.forEach(option => {
        if (!newSelectedOptions.includes(option)) {
          newSelectedOptions.push(option);
        }
      });
      return newSelectedOptions;
    });
  };

  const Options = useMemo(() => ({
    '2d': {
      name: tFloorPlanLanding('2dFloorPlan'),
      price: 7,
    },
    '2d_furniture': {
      name: tFloorPlanLanding('fixedFurniture'),
      price: 5,
    },
    '8h': {
      name: tFloorPlanLanding('8hTurnaround'),
      price: 10,
    },
    '3d': {
      name: tFloorPlanLanding('3dFloorPlan'),
      price: 10,
    },
    '3d_furniture': {
      name: tFloorPlanLanding('furniture'),
      price: 5,
    },
    '3d_video_render': {
      name: tFloorPlanLanding('3dVideoRender'),
      price: 60,
    },
    cad: {
      name: tFloorPlanLanding('cadFiles'),
      price: 60,
    },
    '2dRender': {
      name: tFloorPlanLanding('2dStillInteriorRendering'),
      price: 5,
    },
    panoramicRender: {
      name: tFloorPlanLanding('panoramicInteriorRendering'),
      price: 5,
    },
    vrTour: {
      name: tFloorPlanLanding('vrTour'),
      price: 24,
    },
    interactiveFloorPlan: {
      name: tFloorPlanLanding('interactiveFloorPlan'),
      price: 24,
    },
  }), [tFloorPlanLanding]);

  return (
    <div className={styles.pricing} id="pricing">
      <div className="container">
        <div className={styles.pricingTitle}>
          {tFloorPlanLanding('pricing')}
        </div>
        <div className={styles.pricingSubtitle}>
          {tFloorPlanLanding('pricingSubtitle')}
        </div>
        <div className={styles.pricingContent}>
          <div className={styles.pricingCards}>
            <div className={styles.pricingCard}>
              <img className={styles.pricingCardImg} src={pricing1Img} alt="pricing" />
              <div className={styles.pricingOptions}>
                <PricingOption
                  title={Options['2d'].name}
                  price={Options['2d'].price}
                  checked={selectedOptions.includes('2d')}
                  onClick={() => toggleOption('2d')}
                />
                <PricingOption
                  title={Options['2d_furniture'].name}
                  price={Options['2d_furniture'].price}
                  checked={selectedOptions.includes('2d_furniture')}
                  onClick={() => {
                    toggleOption('2d_furniture');

                    if (!selectedOptions.includes('2d')) {
                      ensureOption(['2d']);
                    }
                  }}
                />
                <PricingOption
                  title={Options['8h'].name}
                  price={Options['8h'].price}
                  checked={selectedOptions.includes('8h')}
                  onClick={() => {
                    toggleOption('8h');

                    if (!selectedOptions.includes('2d')) {
                      ensureOption(['2d']);
                    }
                  }}
                />
              </div>
            </div>
            <div className={styles.pricingCard}>
              <img className={styles.pricingCardImg} src={threeDImage} alt="pricing" />
              <div className={styles.pricingOptions}>
                <PricingOption
                  title={Options['3d'].name}
                  price={Options['3d'].price}
                  checked={selectedOptions.includes('3d')}
                  onClick={() => toggleOption('3d')}
                />
                <PricingOption
                  title={Options['3d_furniture'].name}
                  price={Options['3d_furniture'].price}
                  checked={selectedOptions.includes('3d_furniture')}
                  onClick={() => {
                    toggleOption('3d_furniture');

                    if (!selectedOptions.includes('3d')) {
                      ensureOption(['3d']);
                    }
                  }}
                />
              </div>
            </div>
            <div className={styles.pricingCard}>
              <video className={styles.pricingCardImg} src={threeDRenderVideo} alt="pricing" controls autoPlay muted loop />
              <div className={styles.pricingOptions}>
                <PricingOption
                  title={Options['3d_video_render'].name}
                  price={Options['3d_video_render'].price}
                  checked={selectedOptions.includes('3d_video_render')}
                  onClick={() => {
                    toggleOption('3d_video_render');

                    if (!selectedOptions.includes('3d_video_render')) {
                      ensureOption(['3d', '3d_furniture']);
                    }
                  }}
                />
                <div className={styles.pricingCardDescription}>
                  {tFloorPlanLanding('3dVideoRenderSubtitle')}
                </div>
              </div>
            </div>
            <div className={styles.pricingCard}>
              <img className={styles.pricingCardImg} src={pricing5Img} alt="pricing" />
              <div className={styles.pricingOptions}>
                <PricingOption
                  title={Options.cad.name}
                  price={Options.cad.price}
                  checked={selectedOptions.includes('cad')}
                  onClick={() => toggleOption('cad')}
                />
                <div className={styles.pricingCardDescription}>
                  {tFloorPlanLanding('cadFilesSubtitle1')}
                </div>
                <div className={styles.pricingCardDescription}>
                  {tFloorPlanLanding('cadFilesSubtitle2')}
                </div>
              </div>
            </div>
            <div className={styles.pricingCard}>
              <img className={styles.pricingCardImg} src={bedCam2FinalImg} alt="pricing" />
              <div className={styles.pricingOptions}>
                <PricingOption
                  title={Options['2dRender'].name}
                  price={Options['2dRender'].price}
                  checked={selectedOptions.includes('2dRender')}
                  onClick={() => {
                    toggleOption('2dRender');
                    if (!selectedOptions.includes('2dRender')) {
                      ensureOption(['3d', '3d_furniture']);
                    }
                  }}
                />
                <PricingOption
                  title={Options.panoramicRender.name}
                  price={Options.panoramicRender.price}
                  checked={selectedOptions.includes('panoramicRender')}
                  onClick={() => {
                    toggleOption('panoramicRender');
                    if (!selectedOptions.includes('panoramicRender')) {
                      ensureOption(['3d', '3d_furniture']);
                    }
                  }}
                />

              </div>
            </div>
            <div className={styles.pricingCard}>
              <a href="https://diy.betates.com/published-tours/test%20360-1737686830880" target="_blank" rel="noreferrer">
                <img className={styles.pricingCardImg} src={vrTourImg} alt="pricing" />
              </a>
              <div className={styles.pricingOptions}>
                <PricingOption
                  title={Options.vrTour.name}
                  price={Options.vrTour.price}
                  checked={selectedOptions.includes('vrTour')}
                  onClick={() => {
                    toggleOption('vrTour');
                    if (!selectedOptions.includes('vrTour')) {
                      ensureOption(['3d', '3d_furniture', 'panoramicRender']);
                    }
                  }}
                />
                <div className={styles.pricingCardDescription}>
                  {tFloorPlanLanding('vrTourDescription')}
                </div>
              </div>
            </div>
            <div className={styles.pricingCard}>
              <img className={classNames(styles.pricingCardImg, styles.contain)} src={interactiveFloorPlanImg} alt="pricing" />
              <div className={styles.pricingOptions}>
                <PricingOption
                  title={Options.interactiveFloorPlan.name}
                  price={Options.interactiveFloorPlan.price}
                  checked={selectedOptions.includes('interactiveFloorPlan')}
                  onClick={() => {
                    toggleOption('interactiveFloorPlan');
                    if (!selectedOptions.includes('interactiveFloorPlan')) {
                      ensureOption(['2d', '2d_furniture']);
                    }
                  }}
                />
                <div className={styles.pricingCardDescription}>
                  {tFloorPlanLanding('interactiveFloorPlanDescription')}
                </div>
              </div>
            </div>
          </div>
          <div className={styles.pricingSummary}>
            <div className={styles.pricingSummaryTitle}>
              {tFloorPlanLanding('yourSelection')}
            </div>
            <div className={styles.pricingSummaryContent}>
              {selectedOptions.map((option) => (
                <div className={styles.pricingSummaryItem}>
                  <div className={styles.pricingSummaryItemName}>
                    {Options[option].name}
                  </div>
                  <div className={styles.pricingSummaryItemPrice}>
                    $
                    {Options[option].price}
                  </div>
                </div>
              ))}
              <div className={styles.divider} />
              <div className={styles.total}>
                <div className={styles.totalTitle}>
                  {tFloorPlanLanding('total')}
                </div>
                <div className={styles.totalPrice}>
                  $
                  {selectedOptions.reduce((acc, option) => acc + Options[option].price, 0).toFixed(2)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function FloorPlanLanding() {
  const { tFloorPlanLanding } = useTranslation();

  return (
    <div>
      <LandingHeader />
      <div className={classNames(styles.hero, 'container')}>
        <div className={styles.left}>
          <h1 className={styles.heading}>
            {tFloorPlanLanding('heading1')}
            {' '}
            <span>{tFloorPlanLanding('heading2')}</span>
          </h1>
          <h2 className={styles.heading3}>{tFloorPlanLanding('heading3')}</h2>

          <p className={styles.heading4}>{tFloorPlanLanding('heading4')}</p>

          <div className={styles.headingButtons}>
            <Button variant="primary" className={styles.viewPricing} href="#pricing">
              {tFloorPlanLanding('viewPricing')}
            </Button>
            <button
              type="button"
              className={styles.appStoreButton}
              onClick={() => {
                window.open('https://apps.apple.com/us/app/betates-s2p/id6747028712', '_blank');
              }}
            >
              <AppStoreButton />
            </button>
          </div>

          <div className={styles.features}>
            <div className={styles.feature}>
              <HeroBullet1Icon />
              <div className={styles.featureText}>{tFloorPlanLanding('roomMeasurement')}</div>
            </div>
            <div className={styles.feature}>
              <HeroBullet2Icon />
              <div className={styles.featureText}>{tFloorPlanLanding('customFloorPlanStyles')}</div>
            </div>
          </div>
        </div>
        <div className={styles.right}>
          <img className={styles.heroImg} src={heroImg} alt="hero" />
        </div>
      </div>
      <div className={styles.why}>
        <div className={classNames(styles.why1, 'container')}>
          <div className={styles.personal}>
            {tFloorPlanLanding('personal1')}
          </div>
          <div className={styles.usecase}>
            {tFloorPlanLanding('usecase1')}
          </div>
          <div className={styles.usecaseSubtitle}>
            {tFloorPlanLanding('usecase1Subtitle')}
          </div>
          <div className={styles.content}>
            <UseCase1Img className={styles.usecaseImg} />
            <div className={styles.whyContent}>
              <div className={styles.whyTitle}>
                {tFloorPlanLanding('why1Title')}
              </div>
              <div className={styles.whyList}>
                <div className={styles.whyItem}>
                  <div className={styles.whyItemIcon}>
                    <Why1Icon width={44} height={44} />
                  </div>
                  <div className={styles.whyItemText}>{tFloorPlanLanding('why1_1')}</div>
                </div>
                <div className={styles.whyItem}>
                  <div className={styles.whyItemIcon}>
                    <Why2Icon width={44} height={44} />
                  </div>
                  <div className={styles.whyItemText}>{tFloorPlanLanding('why1_2')}</div>
                </div>
                <div className={styles.whyItem}>
                  <div className={styles.whyItemIcon}>
                    <Why3Icon width={44} height={44} />
                  </div>
                  <div className={styles.whyItemText}>{tFloorPlanLanding('why1_3')}</div>
                </div>
                <div className={styles.whyItem}>
                  <div className={styles.whyItemIcon}>
                    <Why4Icon width={44} height={44} />
                  </div>
                  <div className={styles.whyItemText}>{tFloorPlanLanding('why1_4')}</div>
                </div>
                <div className={styles.whyItem}>
                  <div className={styles.whyItemIcon}>
                    <Why5Icon width={44} height={44} />
                  </div>
                  <div className={styles.whyItemText}>{tFloorPlanLanding('why1_5')}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="container">
          <div className={styles.divider} />
        </div>
        <div className={classNames(styles.why2, 'container')}>
          <div className={styles.personal}>
            {tFloorPlanLanding('personal2')}
          </div>
          <div className={styles.usecase}>
            {tFloorPlanLanding('usecase2')}
          </div>
          <div className={styles.usecaseSubtitle}>
            {tFloorPlanLanding('usecase2Subtitle')}
          </div>
          <div className={styles.content}>
            <UseCase2Img className={styles.usecaseImg} />
            <div className={styles.whyContent}>
              <div className={styles.whyTitle}>
                {tFloorPlanLanding('why2Title')}
              </div>
              <div className={styles.whyList}>
                <div className={styles.whyItem}>
                  <div className={styles.whyItemIcon}>
                    <Why1Icon width={44} height={44} />
                  </div>
                  <div className={styles.whyItemText}>{tFloorPlanLanding('why2_1')}</div>
                </div>
                <div className={styles.whyItem}>
                  <div className={styles.whyItemIcon}>
                    <Why2Icon width={44} height={44} />
                  </div>
                  <div className={styles.whyItemText}>{tFloorPlanLanding('why2_2')}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className={styles.howItWorks}>
        <div className="container">
          <div className={styles.hiwTitle}>
            {tFloorPlanLanding('hiwTitle')}
          </div>
          <div className={styles.hiwList}>
            <div className={styles.hiwItem}>
              <Hiw1Img />
              <div className={styles.hiwItemTitle}>
                {tFloorPlanLanding('hiw1Title')}
              </div>
              <div className={styles.hiwItemDescription}>
                {tFloorPlanLanding('hiw1Description')}
              </div>
            </div>
            <div className={styles.hiwItem}>
              <Hiw2Img />
              <div className={styles.hiwItemTitle}>
                {tFloorPlanLanding('hiw2Title')}
              </div>
              <div className={styles.hiwItemDescription}>
                {tFloorPlanLanding('hiw2Description')}
              </div>
            </div>
            <div className={styles.hiwItem}>
              <Hiw3Img />
              <div className={styles.hiwItemTitle}>
                {tFloorPlanLanding('hiw3Title')}
              </div>
              <div className={styles.hiwItemDescription}>
                {tFloorPlanLanding('hiw3Description')}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className={styles.features}>
        <div className="container">
          <div className={styles.featuresTitle}>
            {tFloorPlanLanding('features')}
          </div>
          <div className={styles.featureSubtitle}>
            {tFloorPlanLanding('featuresSubtitle1')}
          </div>
          <div className={styles.featureSubtitle}>
            {tFloorPlanLanding('featuresSubtitle2')}
          </div>
          <div className={styles.featureList}>
            <div className={styles.featureRow}>
              <div className={styles.featureCard2}>
                <div className={styles.featureCardContent}>
                  <div className={styles.featureTitle}>
                    {tFloorPlanLanding('feature1')}
                  </div>
                  <div className={styles.featureBullet}>
                    {tFloorPlanLanding('feature1_1')}
                  </div>
                  <div className={styles.featureBullet}>
                    {tFloorPlanLanding('feature1_2')}
                  </div>
                  <div className={styles.featureBullet}>
                    {tFloorPlanLanding('feature1_3')}
                  </div>
                  <div className={styles.featureBullet}>
                    {tFloorPlanLanding('feature1_4')}
                  </div>
                </div>
                <div className={styles.featureImgContainer}>
                  <img className={styles.featureImg} src={feature1Img} alt="feature" />
                </div>
              </div>
              <div className={styles.featureCard2}>
                <div className={styles.featureCardContent}>
                  <div className={styles.featureTitle}>
                    {tFloorPlanLanding('feature2')}
                  </div>
                  <div className={styles.featureBullet}>
                    {tFloorPlanLanding('feature2_1')}
                  </div>
                  <div className={styles.featureBullet}>
                    {tFloorPlanLanding('feature2_2')}
                  </div>
                  <div className={styles.featureBullet}>
                    {tFloorPlanLanding('feature2_3')}
                  </div>
                  <div className={styles.featureBullet}>
                    {tFloorPlanLanding('feature2_4')}
                  </div>
                  <div className={styles.featureBullet}>
                    {tFloorPlanLanding('feature2_5')}
                  </div>
                </div>
                <div className={styles.featureImgContainer}>
                  <img className={styles.featureImg} src={threeDImage} alt="feature" />
                </div>
              </div>
              <div className={styles.featureCard2}>
                <div className={styles.featureCardContent}>
                  <div className={styles.featureTitle}>
                    {tFloorPlanLanding('feature3')}
                  </div>
                  <div className={styles.featureBullet}>
                    {tFloorPlanLanding('feature3_1')}
                  </div>
                  <div className={styles.featureBullet}>
                    {tFloorPlanLanding('feature3_2')}
                  </div>
                </div>
                <div className={styles.featureImgContainer}>
                  <img className={styles.featureImg} src={feature5Img} alt="feature" />
                </div>
              </div>
            </div>
            <div className={styles.featureRow}>
              <div className={styles.featureCard2}>
                <div className={styles.featureCardContent}>
                  <div className={styles.featureTitle}>
                    {tFloorPlanLanding('feature4')}
                  </div>
                  <div className={styles.featureBullet}>
                    {tFloorPlanLanding('feature4_1')}
                  </div>
                  <div className={styles.featureBullet}>
                    {tFloorPlanLanding('feature4_2')}
                  </div>
                  <div className={styles.featureBullet}>
                    {tFloorPlanLanding('feature4_3')}
                  </div>
                  <div className={styles.featureBullet}>
                    {tFloorPlanLanding('feature4_4')}
                  </div>
                </div>
                <div className={styles.featureImgContainer}>
                  <img className={styles.featureImg} src={bedCam2FinalImg} alt="feature" />
                </div>
              </div>
              <div className={styles.featureCard2}>
                <div className={styles.featureCardContent}>
                  <div className={styles.featureTitle}>
                    {tFloorPlanLanding('feature5')}
                  </div>
                  <div className={styles.featureBullet}>
                    {tFloorPlanLanding('feature5_1')}
                  </div>
                  <div className={styles.featureBullet}>
                    {tFloorPlanLanding('feature5_2')}
                  </div>
                  <div className={styles.featureBullet}>
                    {tFloorPlanLanding('feature5_3')}
                  </div>
                  <div className={styles.featureBullet}>
                    {tFloorPlanLanding('feature5_4')}
                  </div>
                </div>
                <div className={styles.featureImgContainer}>
                  <a href="https://diy.betates.com/published-tours/test%20360-1737686830880" target="_blank" rel="noreferrer">
                    <img className={styles.featureImg} src={vrTourImg} alt="feature" />
                  </a>
                </div>
              </div>
              <div className={styles.featureCard2}>
                <div className={styles.featureCardContent}>
                  <div className={styles.featureTitle}>
                    {tFloorPlanLanding('feature6')}
                  </div>
                  <div className={styles.featureBullet}>
                    {tFloorPlanLanding('feature6_1')}
                  </div>
                  <div className={styles.featureBullet}>
                    {tFloorPlanLanding('feature6_2')}
                  </div>
                  <div className={styles.featureBullet}>
                    {tFloorPlanLanding('feature6_3')}
                  </div>
                  <div className={styles.featureBullet}>
                    {tFloorPlanLanding('feature6_4')}
                  </div>
                  <div className={styles.featureBullet}>
                    {tFloorPlanLanding('feature6_5')}
                  </div>
                </div>
                <div className={styles.featureImgContainer}>
                  <img className={classNames(styles.featureImg, styles.contain)} src={interactiveFloorPlanImg} alt="feature" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Pricing />
      <LandingFooter />
    </div>
  );
}
